// Settings → Rejected station actions (plan §9.3 "rejected commands are kept and shown to
// the station's manager", §10.4): what the server refused for good — someone without the
// permission, a staff member it doesn't know, details that didn't make sense. Read-only:
// nothing was recorded and there is nothing to resolve; the station says "see the Admin".
// Same standard as held actions (review UI3A-2, 9, 10): reasons in words, when it happened
// first with a late arrival marked, mono only for the device code.

import type { RejectedCommandSummary } from '@enp/domain/api';
import {
  EmptyState,
  PageHeader,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight, PartyPopper } from 'lucide-react';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { commandLabel, rejectReason } from '../../../../stations/labels.js';
import {
  ActionsListSkeleton,
  DeviceName,
  HappenedCell,
  NoDeviceAccess,
  SettingsBreadcrumb,
  SettingsFrame,
  useCanManageDevices,
} from '../../../../stations/page-parts.js';
import { rejectedQuery } from '../../../../stations/queries.js';

export const Route = createFileRoute('/_app/settings/rejected-actions/')({
  component: RejectedActionsPage,
});

function RejectedRow({ item }: { item: RejectedCommandSummary }) {
  const staff = item.staff?.display_name ?? 'Unknown';
  return (
    <TableRow className="relative hover:bg-surface-hover">
      <TableCell>
        <HappenedCell occurredAt={item.occurred_at} receivedAt={item.received_at} />
      </TableCell>
      <TableCell>
        <span className="flex flex-col leading-tight">
          <Link
            to="/settings/rejected-actions/$commandId"
            params={{ commandId: item.command_id }}
            className="font-medium text-fg after:absolute after:inset-0 hover:underline"
          >
            {commandLabel(item.type)}
          </Link>
          <span className="text-xs text-muted lg:hidden">By {staff}</span>
        </span>
      </TableCell>
      <TableCell>
        <DeviceName code={item.device.device_code} station={item.device.station_name} />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {item.staff?.display_name ?? <span className="text-muted">Unknown</span>}
      </TableCell>
      <TableCell>{rejectReason(item.reason).label}</TableCell>
      <TableCell className="w-8">
        <ChevronRight aria-hidden className="size-4 text-muted" />
      </TableCell>
    </TableRow>
  );
}

function RejectedActionsPage() {
  useDocumentTitle('Rejected station actions');
  const canManage = useCanManageDevices();
  const list = useQuery({ ...rejectedQuery(), enabled: canManage === true });
  const items = list.data?.items ?? [];

  return (
    <SettingsFrame>
      <SettingsBreadcrumb page="Rejected station actions" />
      <PageHeader
        title="Rejected station actions"
        description="Actions a station sent that the server refused for good, so they were never recorded — for example, done by someone without the permission. Nothing needs resolving here: see what happened, sort out the cause, and do it again at the station if it is still needed."
      />
      {canManage === false ? (
        <NoDeviceAccess />
      ) : list.isError ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The rejected actions"
          onRetry={() => void list.refetch()}
        />
      ) : !list.data ? (
        <ActionsListSkeleton label="Loading rejected actions" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={PartyPopper}
          tone="success"
          title="Nothing was refused"
          description="When the server refuses an action a station sent, it appears here with who, when and why."
        />
      ) : (
        <>
          <Table>
            <TableCaption className="sr-only">Rejected station actions</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Happened</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Device</TableHead>
                <TableHead className="hidden lg:table-cell">Staff</TableHead>
                <TableHead>Why it was refused</TableHead>
                <TableHead>
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <RejectedRow key={r.command_id} item={r} />
              ))}
            </TableBody>
          </Table>
          {list.data.truncated && (
            <p className="mt-3 text-sm text-muted">Showing the newest {items.length}.</p>
          )}
        </>
      )}
    </SettingsFrame>
  );
}
